import { Box, Divider, Stack, Typography } from '@mui/material';
import { concat, get } from 'lodash';
import React, { FC } from 'react';
import CreateTable from '../../../../../../../../components/ui-component/form-component/create-form/CreateTable';
import { useApplication } from '../../../../../../../../context/ApplicationContext';
import { FormStrucItem } from '../../../../../../../../lib/types/types';
import { NonRequired } from '../../../../../../../../lib/validation/ValidationTypes';
import { addIndexesToPath, isTrue } from '../../../../../../../../utils/helper';

// import schemas
import Schema from '../../../../../../../../lib/paths/abschlussbericht_FULL_Paths.json';
const P = Schema.attributes;

const EffekteDerFoerderungAbschlussEMob: FC<{ kostentraeger: string; unternehmenId: number }> = ({ kostentraeger, unternehmenId }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();

	return (
		<Box sx={{ mt: '1rem' }}>
			{/* eslint-disable-next-line array-callback-return */}
			{get(application, P.Kurzfassung_Zum_Projektende.path, []).map((_ele: any, i: number) => {
				const kostentraegerPath = addIndexesToPath(
					P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Kostentraeger.path,
					[`${i}`],
					['Effekte_Der_Foerderung_Im_Unternehmen']
				);
				const antragstellerPath = addIndexesToPath(
					P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Antragsteller.path,
					[`${i}`],
					['Effekte_Der_Foerderung_Im_Unternehmen']
				);
				const partnerPath = addIndexesToPath(
					P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Verbundpartner.path,
					[`${i}`],
					['Effekte_Der_Foerderung_Im_Unternehmen']
				);
				if (
					get(application, kostentraegerPath) === kostentraeger &&
					(get(application, `${antragstellerPath}.id`) === unternehmenId || get(application, `${partnerPath}.id`) === unternehmenId)
				) {
					return (
						<Stack spacing={4} key={i}>
							<Box>
								<Typography variant='h6'>Aussagen zum erzielten Projektergebnis in Ihrem Unternehmen</Typography>
								<Divider />
								<CreateTable
									formStruc={mapFormStruc(formStrucProjektergebnis(), i)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={readOnly}
									columnsLabel={COLUMNS_LABEL}
								/>
							</Box>
							<Box>
								<Typography variant='h6'>Aussagen zur erzielten Beschäftigungswirkung im Unternehmen</Typography>
								<CreateTable
									formStruc={mapFormStruc(formStrucBeschaeftigung(application, i), i)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={readOnly}
									columnsLabel={COLUMNS_LABEL_MIT_ANZAHL}
								/>
							</Box>

							<Box>
								<Typography variant='h6'>
									Welche ökonomische Wirkung in Ihrem Unternehmen führen Sie auf das Projektergebnis zurück?
								</Typography>
								<CreateTable
									formStruc={mapFormStruc(formStrucOekonomishe(), i)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={readOnly}
									columnsLabel={COLUMNS_LABEL}
								/>
							</Box>

							<Box>
								<Typography variant='h6'>Welche ökologische Wirkung im Unternehmen führen Sie auf das Projektergebnis zurück?</Typography>
								<CreateTable
									formStruc={mapFormStruc(formStrucOekologische(), i)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={readOnly}
									columnsLabel={COLUMNS_LABEL}
								/>
							</Box>

							<Box>
								<Typography variant='h6'>Welche Bedeutung hat die in Anspruch genommene Förderung für Ihre F&E-Aktivitäten?</Typography>
								<CreateTable
									formStruc={mapFormStruc(formStrucBedeutung(), i)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={readOnly}
									columnsLabel={COLUMNS_LABEL}
								/>
							</Box>

							<Box>
								<Typography variant='h6'>Schutzrechte, Normen und Standards</Typography>
								<CreateTable
									formStruc={mapFormStruc(formStrucSchutzRechte(application, i), i)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={readOnly}
									columnsLabel={COLUMNS_LABEL_MIT_ANZAHL}
								/>
							</Box>

							<Box>
								<Typography variant='h6'>Marketing und Öffentlichkeitsarbeit</Typography>
								<CreateTable
									formStruc={mapFormStruc(formStrucMarketing(application, i), i)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={readOnly}
									columnsLabel={COLUMNS_LABEL_MIT_ANZAHL}
								/>
							</Box>

							<Box>
								<Typography variant='h6'>Bewertung der Zusammenarbeit im Projekt</Typography>
								<CreateTable
									formStruc={mapFormStruc(formStrucZusammenarbeit(), i)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={readOnly}
									columnsLabel={COLUMNS_LABEL}
								/>
							</Box>

							<Box>
								<Typography variant='h6'>Bewertung der Abwicklung durch die Hessen Agentur</Typography>
								<CreateTable
									formStruc={mapFormStruc(formStrucAbwicklung(), i)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={readOnly}
									columnsLabel={COLUMNS_LABEL}
								/>
							</Box>
						</Stack>
					);
				}
			})}
		</Box>
	);
};

export default EffekteDerFoerderungAbschlussEMob;

const COLUMNS_LABEL = ['', 'Angabe'];
const COLUMNS_LABEL_MIT_ANZAHL = ['', 'Angabe', { label: 'Anzahl', width: '14rem' }];

const formStrucProjektergebnis = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0006720802_faehiges_Projektergebnis_Vor.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0006720802_faehiges_Projektergebnis_Vor,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0243653589_chaftlich_Verwertet_Prognose.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0243653589_chaftlich_Verwertet_Prognose,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0126883988_ne_Umsatzsteigerung_Erwartet.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0126883988_ne_Umsatzsteigerung_Erwartet,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
			],
		},
	];
	return struc;
};
const formStrucBeschaeftigung = (application: any, i: number) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0023180645_eitsplaetze_Gesichert_Angabe.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0023180645_eitsplaetze_Gesichert_Angabe,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
				...((
					isTrue(
						get(
							application,
							addIndexesToPath(
								P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0023180645_eitsplaetze_Gesichert_Angabe
									.path,
								[`${i}`],
								['Effekte_Der_Foerderung_Im_Unternehmen']
							)
						)
					)
				) ?
					[P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0023160076_eitsplaetze_Gesichert_Anzahl]
				:	[]),
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0106457125_laetze_Neu_Geschaffen_Angabe.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0106457125_laetze_Neu_Geschaffen_Angabe,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
				...((
					isTrue(
						get(
							application,
							addIndexesToPath(
								P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0106457125_laetze_Neu_Geschaffen_Angabe
									.path,
								[`${i}`],
								['Effekte_Der_Foerderung_Im_Unternehmen']
							)
						)
					)
				) ?
					[P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0106608716_laetze_Neu_Geschaffen_Anzahl]
				:	[]),
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0187631509_Entwicklung_Gesichert_Angabe.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0187631509_Entwicklung_Gesichert_Angabe,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
				...((
					isTrue(
						get(
							application,
							addIndexesToPath(
								P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0187631509_Entwicklung_Gesichert_Angabe
									.path,
								[`${i}`],
								['Effekte_Der_Foerderung_Im_Unternehmen']
							)
						)
					)
				) ?
					[P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0187684860_Entwicklung_Gesichert_Anzahl]
				:	[]),
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0077734581_cklung_Neu_Geschaffen_Angabe.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0077734581_cklung_Neu_Geschaffen_Angabe,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
				...((
					isTrue(
						get(
							application,
							addIndexesToPath(
								P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0077734581_cklung_Neu_Geschaffen_Angabe
									.path,
								[`${i}`],
								['Effekte_Der_Foerderung_Im_Unternehmen']
							)
						)
					)
				) ?
					[P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0077918940_cklung_Neu_Geschaffen_Anzahl]
				:	[]),
			],
		},
	];

	return struc;
};
const formStrucOekonomishe = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0102140772_B_Vorsprung_Im_Kerngeschaeft.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0102140772_B_Vorsprung_Im_Kerngeschaeft,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0099476942_echnologiefelder_Anwendungen.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0099476942_echnologiefelder_Anwendungen,
			],
		},
	];
	return struc;
};
const formStrucOekologische = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0072507998_nteils_Erneuerbarer_Energien.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0072507998_nteils_Erneuerbarer_Energien,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0238495994_sourcen_Und_Energieeffizienz.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0238495994_sourcen_Und_Energieeffizienz,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0161014414_hadstoffhaltiger_Materialien.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0161014414_hadstoffhaltiger_Materialien,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0167607314_rmeidung_Von_Abfall_Abwasser.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0167607314_rmeidung_Von_Abfall_Abwasser,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0113368453_bnis_Handelt_Es_Sich_Um_Eine.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0113368453_bnis_Handelt_Es_Sich_Um_Eine,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0198362590_uzierung_Der_CO_2_Emissionen.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0198362590_uzierung_Der_CO_2_Emissionen,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucBedeutung = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0249725438_ls_Ein_FE_Vorhaben_Betrieben.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0249725438_ls_Ein_FE_Vorhaben_Betrieben,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0147735278_tzliche_FE_Vorhaben_Begonnen.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0147735278_tzliche_FE_Vorhaben_Begonnen,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0046711253_icht_Realisiert_Worden_Waere.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0046711253_icht_Realisiert_Worden_Waere,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0246206436_kante_FE_Vorhaben_Investiert.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0246206436_kante_FE_Vorhaben_Investiert,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0192425326_t_Fuer_FE_Planungen_Bekommen.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0192425326_t_Fuer_FE_Planungen_Bekommen,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0175863134_ration_Mit_Partnern_Begonnen.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0175863134_ration_Mit_Partnern_Begonnen,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0087297278_Fuer_Kooperationen_Gefunden.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0087297278_Fuer_Kooperationen_Gefunden,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0150001988_Idee_Und_Umsetzung_Verkuerzt.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0150001988_Idee_Und_Umsetzung_Verkuerzt,
			],
		},
	];
	return struc;
};
const formStrucSchutzRechte = (application: any, i: number) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0181036085_Neu_Angemeldet_Werden_Angabe.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0181036085_Neu_Angemeldet_Werden_Angabe,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
				...((
					isTrue(
						get(
							application,
							addIndexesToPath(
								P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0181036085_Neu_Angemeldet_Werden_Angabe
									.path,
								[`${i}`],
								['Effekte_Der_Foerderung_Im_Unternehmen']
							)
						)
					)
				) ?
					[P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0181187676_Neu_Angemeldet_Werden_Anzahl]
				:	[]),
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0029372821_Neu_Angemeldet_Werden_Angabe.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0029372821_Neu_Angemeldet_Werden_Angabe,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
				...((
					isTrue(
						get(
							application,
							addIndexesToPath(
								P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0029372821_Neu_Angemeldet_Werden_Angabe
									.path,
								[`${i}`],
								['Effekte_Der_Foerderung_Im_Unternehmen']
							)
						)
					)
				) ?
					[P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0029549052_Neu_Angemeldet_Werden_Anzahl]
				:	[]),
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0211842869_dukt_Umgesetzt_Werden_Angabe.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0211842869_dukt_Umgesetzt_Werden_Angabe,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
				...((
					isTrue(
						get(
							application,
							addIndexesToPath(
								P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0211842869_dukt_Umgesetzt_Werden_Angabe
									.path,
								[`${i}`],
								['Effekte_Der_Foerderung_Im_Unternehmen']
							)
						)
					)
				) ?
					[P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0211986268_dukt_Umgesetzt_Werden_Anzahl]
				:	[]),
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0113427445_rds_Geschaffen_Werden_Angabe.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0113427445_rds_Geschaffen_Werden_Angabe,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
				...((
					isTrue(
						get(
							application,
							addIndexesToPath(
								P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0113427445_rds_Geschaffen_Werden_Angabe
									.path,
								[`${i}`],
								['Effekte_Der_Foerderung_Im_Unternehmen']
							)
						)
					)
				) ?
					[P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0113513372_rds_Geschaffen_Werden_Anzahl]
				:	[]),
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0036072293_t_Fuer_Das_Vorhaben_Nicht_Zu.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0036072293_t_Fuer_Das_Vorhaben_Nicht_Zu,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
			],
		},
	];
	return struc;
};
const formStrucMarketing = (application: any, i: number) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0181707621_Auf_Nationalen_Messen_Angabe.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0181707621_Auf_Nationalen_Messen_Angabe,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
				...((
					isTrue(
						get(
							application,
							addIndexesToPath(
								P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0181707621_Auf_Nationalen_Messen_Angabe
									.path,
								[`${i}`],
								['Effekte_Der_Foerderung_Im_Unternehmen']
							)
						)
					)
				) ?
					[P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0181826316_Auf_Nationalen_Messen_Anzahl]
				:	[]),
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0163351765_nternationalen_Messen_Angabe.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0163351765_nternationalen_Messen_Angabe,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
				...((
					isTrue(
						get(
							application,
							addIndexesToPath(
								P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0163351765_nternationalen_Messen_Angabe
									.path,
								[`${i}`],
								['Effekte_Der_Foerderung_Im_Unternehmen']
							)
						)
					)
				) ?
					[P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0163470524_nternationalen_Messen_Anzahl]
				:	[]),
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0151843013_llung_Auf_Konferenzen_Angabe.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0151843013_llung_Auf_Konferenzen_Angabe,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
				...((
					isTrue(
						get(
							application,
							addIndexesToPath(
								P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0151843013_llung_Auf_Konferenzen_Angabe
									.path,
								[`${i}`],
								['Effekte_Der_Foerderung_Im_Unternehmen']
							)
						)
					)
				) ?
					[P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0151920812_llung_Auf_Konferenzen_Anzahl]
				:	[]),
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0106982533_chungen_In_Fachmedien_Angabe.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0106982533_chungen_In_Fachmedien_Angabe,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
				...((
					isTrue(
						get(
							application,
							addIndexesToPath(
								P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0106982533_chungen_In_Fachmedien_Angabe
									.path,
								[`${i}`],
								['Effekte_Der_Foerderung_Im_Unternehmen']
							)
						)
					)
				) ?
					[P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0107134188_chungen_In_Fachmedien_Anzahl]
				:	[]),
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0238463685_In_Allgemeiner_Presse_Angabe.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0238463685_In_Allgemeiner_Presse_Angabe,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
				...((
					isTrue(
						get(
							application,
							addIndexesToPath(
								P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0238463685_In_Allgemeiner_Presse_Angabe
									.path,
								[`${i}`],
								['Effekte_Der_Foerderung_Im_Unternehmen']
							)
						)
					)
				) ?
					[P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0238582444_In_Allgemeiner_Presse_Anzahl]
				:	[]),
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0099152117_n_Netzwerken_Clustern_Angabe.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0099152117_n_Netzwerken_Clustern_Angabe,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
				...((
					isTrue(
						get(
							application,
							addIndexesToPath(
								P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0099152117_n_Netzwerken_Clustern_Angabe
									.path,
								[`${i}`],
								['Effekte_Der_Foerderung_Im_Unternehmen']
							)
						)
					)
				) ?
					[P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0099229852_n_Netzwerken_Clustern_Anzahl]
				:	[]),
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0156591093_n_Oder_Auszeichnungen_Angabe.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0156591093_n_Oder_Auszeichnungen_Angabe,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
				...((
					isTrue(
						get(
							application,
							addIndexesToPath(
								P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0156591093_n_Oder_Auszeichnungen_Angabe
									.path,
								[`${i}`],
								['Effekte_Der_Foerderung_Im_Unternehmen']
							)
						)
					)
				) ?
					[
						P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0156611484_n_Oder_Auszeichnungen_Anzahl,
						P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0137899669_uszeichnungen_Wenn_Ja_Welche,
					]
				:	[]),
			],
		},
	];
	return struc;
};
const formStrucZusammenarbeit = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0243844020_amt_Als_Erfolgreich_Erachtet.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0243844020_amt_Als_Erfolgreich_Erachtet,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0197794745_ifikanten_Kenntnisgewinn_Bei.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0197794745_ifikanten_Kenntnisgewinn_Bei,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0231695044_Projektpartnern_Fortgefuehrt.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0231695044_Projektpartnern_Fortgefuehrt,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0223104281_ziehung_Mit_Den_Partnern_Bei.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0223104281_ziehung_Mit_Den_Partnern_Bei,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0147772174_Strategischer_Kooperationen.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0147772174_Strategischer_Kooperationen,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0241602894_n_Wuerden_Wir_Erneut_Starten.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0241602894_n_Wuerden_Wir_Erneut_Starten,
			],
		},
	];
	return struc;
};
const formStrucAbwicklung = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0185909716_Waehrend_Der_Projektlaufzeit.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0185909716_Waehrend_Der_Projektlaufzeit,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0164571876_Waehrend_Der_Projektlaufzeit.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0164571876_Waehrend_Der_Projektlaufzeit,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0232600900_ngen_An_Das_Projekt_Erfuellt.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0232600900_ngen_An_Das_Projekt_Erfuellt,
			],
		},
		{
			fields: [
				{
					path: P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0232600900_ngen_An_Das_Projekt_Erfuellt.path,
					type: 'label',
					validation: [{ validateFunction: NonRequired }],
				},
				{
					...P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Effekte_Der_Foerderung_I_0232600900_ngen_An_Das_Projekt_Erfuellt,
					type: 'radio',
					validation: [{ validateFunction: NonRequired }],
				},
			],
		},
	];
	return struc;
};

const mapFormStruc = (formStruc: any[], i: number) => {
	return formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((field) => {
				let pathWithoutIndex = get(field, 'path', '') as string;
				return {
					...field,
					path:
						pathWithoutIndex.slice(0, P.Effekte_Der_Foerderung_Im_Unternehmen.path.length) +
						`.${i}` +
						pathWithoutIndex.slice(P.Effekte_Der_Foerderung_Im_Unternehmen.path.length),
				};
			}),
		};
	});
};

export const getFormStrucEffekteDerFoerderungAbschlussEMob = (application: any, kostentraeger: string, unternehmenId: number) => {
	let result: FormStrucItem[] = [];
	// eslint-disable-next-line array-callback-return
	get(application, P.Kurzfassung_Zum_Projektende.path, []).map((_ele: any, i: number) => {
		const kostentraegerPath = addIndexesToPath(
			P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Kostentraeger.path,
			[`${i}`],
			['Effekte_Der_Foerderung_Im_Unternehmen']
		);
		const antragstellerPath = addIndexesToPath(
			P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Antragsteller.path,
			[`${i}`],
			['Effekte_Der_Foerderung_Im_Unternehmen']
		);
		const partnerPath = addIndexesToPath(
			P.Effekte_Der_Foerderung_Im_Unternehmen.schema.attributes.Verbundpartner.path,
			[`${i}`],
			['Effekte_Der_Foerderung_Im_Unternehmen']
		);
		if (
			get(application, kostentraegerPath) === kostentraeger &&
			(get(application, `${antragstellerPath}.id`) === unternehmenId || get(application, `${partnerPath}.id`) === unternehmenId)
		) {
			const formStrucProjektergebnisWithIndex = mapFormStruc(formStrucProjektergebnis(), i);
			const formStrucBeschaeftigungWithIndex = mapFormStruc(formStrucBeschaeftigung(application, i), i);
			const formStrucOekonomisheWithIndex = mapFormStruc(formStrucOekonomishe(), i);
			const formStrucOekologischeWithIndex = mapFormStruc(formStrucOekologische(), i);
			const formStrucBedeutungWithIndex = mapFormStruc(formStrucBedeutung(), i);
			const formStrucSchutzRechteWithIndex = mapFormStruc(formStrucSchutzRechte(application, i), i);
			const formStrucMarketingWithIndex = mapFormStruc(formStrucMarketing(application, i), i);
			const formStrucZusammenarbeitWithIndex = mapFormStruc(formStrucZusammenarbeit(), i);
			const formStrucAbwicklungWithIndex = mapFormStruc(formStrucAbwicklung(), i);
			result = concat(
				formStrucProjektergebnisWithIndex,
				formStrucBeschaeftigungWithIndex,
				formStrucOekonomisheWithIndex,
				formStrucOekologischeWithIndex,
				formStrucBedeutungWithIndex,
				formStrucSchutzRechteWithIndex,
				formStrucMarketingWithIndex,
				formStrucZusammenarbeitWithIndex,
				formStrucAbwicklungWithIndex
			);
		}
	});
	return result;
};
